//shared/hooks/auth-hook
import { useState, useCallback, useEffect } from "react";

let logoutTimer;

export const useAuth = () => {
  const [token, setToken] = useState(false);
  const [tokenExpirationDate, setTokenExpirationDate] = useState();
  const [userId, setUserId] = useState(false);
  const [userRole, setUserRole] = useState(false);
  const [listaRoles, setListaRoles] = useState(null);
  const [image, setImage] = useState("");
  const [clinica, setClinica] = useState(null);
  const [permissoes, setPermissoes] = useState(null);
  const [usuariosAssociados, setUsuariosAssociados] = useState(null);
  const [secretariasDoMedico, setSecretariasDoMedico] = useState(null);
  const [assistentesDoMedico, setAssistentesDoMedico] = useState(null);

  const [nome, setNome] = useState(null);
  const [genero, setGenero] = useState(null);
  const [playNotificationSound, setPlayNotificationSound] = useState(null);
  // Unidade (estabelecimento) em que a sessão decorre. Null nas clínicas de
  // unidade única, que é o caso de quase todas.
  const [estabelecimento, setEstabelecimento] = useState(null);

  const login = useCallback(
    (
      uid,
      token,
      image,
      role,
      listaRoles,
      clinica,
      usuariosAssociados,
      secretariasDoMedico,
      assistentesDoMedico,
      permissoes,
      nome,
      genero,
      playNotificationSound,
      expirationDate,
      estabelecimento,
    ) => {
      setToken(token);
      setEstabelecimento(estabelecimento || null);
      setUserId(uid);
      setImage(image);
      setUserRole(role);
      setListaRoles(listaRoles);
      setClinica(clinica);
      setUsuariosAssociados(usuariosAssociados);
      setSecretariasDoMedico(secretariasDoMedico);
      setAssistentesDoMedico(assistentesDoMedico);
      setPermissoes(permissoes);
      setNome(nome);
      setGenero(genero);
      setPlayNotificationSound(playNotificationSound ? true : false);
      //generating a date object that corresponds to 1hour
      const tokenExpDate =
        expirationDate || new Date(new Date().getTime() + 10000 * 60 * 60);
      setTokenExpirationDate(tokenExpDate);
      //can only write text so we use json stringify to convert the objects to text
      localStorage.setItem(
        "userData",
        JSON.stringify({
          userId: uid,
          token: token,
          image: image,
          role: role,
          listaRoles: listaRoles,
          clinica: clinica,
          usuariosAssociados: usuariosAssociados,
          secretariasDoMedico: secretariasDoMedico,
          assistentesDoMedico: assistentesDoMedico,
          perm: permissoes,
          nome: nome,
          genero: genero,
          playNotificationSound: playNotificationSound,
          estabelecimento: estabelecimento || null,
          //a special kind of string that stores all the important date information that can
          //be converted to a date object later
          expiration: tokenExpDate.toISOString(),
        }),
      );
    },
    [],
  );

  const logout = useCallback(() => {
    /* Avisar o servidor ANTES de limpar, e sem esperar pela resposta.
     *
     * O token é lido do localStorage e não do estado de propósito: assim este
     * `useCallback` continua sem dependências e o temporizador de expiração,
     * que depende da identidade desta função, não se reinicia a cada render.
     *
     * `keepalive` para o pedido sobreviver à navegação que vem a seguir, e um
     * `catch` vazio porque um registo que falha nunca pode impedir alguém de
     * sair. */
    try {
      const guardado = JSON.parse(localStorage.getItem("userData") || "null");
      if (guardado?.token) {
        fetch(`${process.env.REACT_APP_BACKEND_LINK}/api/users/logout`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: "Bearer " + guardado.token },
        }).catch(() => {});
      }
    } catch {
      // localStorage ilegível — sair na mesma.
    }

    setToken(null);
    setTokenExpirationDate(null);
    setUserId(null);
    setImage(null);
    setUserRole(null);
    setListaRoles(null);
    setClinica(null);
    setUsuariosAssociados(null);
    setSecretariasDoMedico(null);
    setAssistentesDoMedico(null);
    setPermissoes(null);
    setNome(null);
    setGenero(null);
    setPlayNotificationSound(null);
    setEstabelecimento(null);
    localStorage.removeItem("userData");
    //Limpar também active doctors id
    localStorage.removeItem("activeDoctorIds");
  }, []);

  // Troca de unidade sem sair da sessão. O backend reemite o token com o novo
  // estabelecimento (mantendo a expiração), e aqui só sincronizamos o estado e
  // o localStorage — sem passar pelo `login`, que repõe tudo o resto.
  const atualizarEstabelecimento = useCallback((novoToken, novoEstab) => {
    setToken(novoToken);
    setEstabelecimento(novoEstab || null);

    const storedData = JSON.parse(localStorage.getItem("userData"));
    if (!storedData) return;

    localStorage.setItem(
      "userData",
      JSON.stringify({
        ...storedData,
        token: novoToken,
        estabelecimento: novoEstab || null,
      }),
    );
  }, []);

  //Controll de login time
  useEffect(() => {
    if (token && tokenExpirationDate) {
      //because setTimeout Wants the duration in miliseconds we calculate it here
      const remainingTime =
        tokenExpirationDate.getTime() - new Date().getTime();
      logoutTimer = setTimeout(logout, remainingTime);
    } else {
      clearTimeout(logoutTimer);
    }
  }, [token, logout, tokenExpirationDate]);

  //react will mount this component, run the code below use effect and the run use Effect
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("userData"));
    if (
      storedData &&
      storedData.token &&
      new Date(storedData.expiration) > new Date()
    ) {
      login(
        storedData.userId,
        storedData.token,
        storedData.image,
        storedData.role,
        storedData.listaRoles,
        storedData.clinica,
        storedData.usuariosAssociados,
        storedData.secretariasDoMedico,
        storedData.assistentesDoMedico,
        storedData.perm,
        storedData.nome,
        storedData.genero,
        storedData.playNotificationSound,
        new Date(storedData.expiration),
        storedData.estabelecimento,
      );
    }
  }, [login]);

  return {
    token,
    login,
    logout,
    userId,
    image,
    userRole,
    listaRoles,
    clinica,
    usuariosAssociados,
    secretariasDoMedico,
    assistentesDoMedico,
    permissoes,
    nome,
    genero,
    playNotificationSound,
    estabelecimento,
    atualizarEstabelecimento,
  };
};
